import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useAutosizeTextArea } from './use-autosize-textarea';
import './styles.css';

export interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  /**
   * Accessible name for the textarea (WCAG 3.3.2 / 4.1.2). A placeholder is
   * not a reliable accessible name — it disappears on input and some screen
   * readers ignore it — so we always expose an explicit label. Falls back to
   * the placeholder, then a generic "Message".
   */
  ariaLabel?: string;
  disabled?: boolean;
  /**
   * Speech-recognition button slot. Rendered in place of the send button
   * when the textarea is empty AND `value` is empty. Consumers wire their
   * own RecognitionButton / web-speech wrapper inside this slot. When
   * omitted, the send button always shows (disabled when value is empty).
   */
  speechButton?: ReactNode;
  /**
   * Visual cue that speech is being captured — applies the listening glow
   * to the textarea. Consumers track listening state in their speech
   * implementation and toggle this prop.
   */
  isListening?: boolean;
  className?: string;
}

export interface ChatInputHandle {
  focus(): void;
}

/**
 * Pill-shaped chat input with an action button at the right. Used by the
 * main MBAssist app footer, the embed link/widget surfaces, and any future
 * consumer of @mbassist/chat-surface.
 *
 * Speech support is opt-in via the `speechButton` render slot — the
 * component stays speech-implementation-agnostic so consumers can choose
 * between react-speech-recognition, the Web Speech API directly, or any
 * other engine. When `speechButton` is provided AND `value` is empty, the
 * slot renders in place of the send button (matching the main app's
 * historical UX where the mic and send swap based on text presence).
 *
 * `forwardRef` exposes a `focus()` handle so consumers can re-focus the
 * textarea after sending a message.
 */
export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  function ChatInput(
    {
      value,
      onChange,
      onSend,
      placeholder,
      ariaLabel,
      disabled = false,
      speechButton,
      isListening = false,
      className,
    },
    ref,
  ) {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    useAutosizeTextArea(textAreaRef.current, value);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => textAreaRef.current?.focus(),
      }),
      [],
    );

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.code === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (value.trim() && !disabled) onSend();
      }
    };

    const showSpeechSlot = !!speechButton && (value.length === 0 || isListening);

    const containerClassName = [
      'cs-chat-input',
      isListening ? 'cs-chat-input--listening' : '',
      className ?? '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClassName}>
        <textarea
          ref={textAreaRef}
          className="cs-chat-input__textarea"
          rows={1}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel ?? placeholder ?? 'Message'}
          disabled={disabled || isListening}
        />
        {showSpeechSlot ? (
          <div className="cs-chat-input__speech-slot">{speechButton}</div>
        ) : (
          <button
            type="button"
            className="cs-chat-input__action"
            aria-label="Send message"
            onClick={onSend}
            disabled={disabled || value.trim().length === 0}
          >
            <svg
              className="cs-chat-input__action-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          </button>
        )}
      </div>
    );
  },
);
